import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const LOCAL_PREFIXES = [
  '/api/public/literature-figures/',
  '/api/auth/chat/attachments/',
];

const figureUrl = (href) => {
  if (!href) return '';
  if (href.startsWith('http://') || href.startsWith('https://')) {
    try {
      return new URL(href).pathname;
    } catch (err) {
      return href;
    }
  }
  return href.startsWith('/') ? href : `/${href}`;
};

const isLocalMedia = (src) => {
  if (!src) return false;
  if (src.startsWith('blob:') || src.startsWith('data:')) return true;
  const path = figureUrl(src);
  return LOCAL_PREFIXES.some((prefix) => path.startsWith(prefix));
};

const inferLocalFigure = (content, alt) => {
  const hay = `${content || ''} ${alt || ''}`.toLowerCase();
  if (hay.includes('annurev-marine') || (hay.includes('voolstra') && hay.includes('2025'))) {
    return '/api/public/literature-figures/voolstra-2025-standardized-methods-p1-i1/';
  }
  if (hay.includes('evensen') || hay.includes('lom3.10555')) {
    return '/api/public/literature-figures/evensen-2021-cbass-p4-i1/';
  }
  if (hay.includes('gcb.15148') || (hay.includes('voolstra') && hay.includes('2020'))) {
    return '/api/public/literature-figures/voolstra-2020-microhabitat-p1-i1/';
  }
  return '';
};

const rewriteMarkdown = (content) => (
  (content || '').replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (match, alt, url) => {
    const href = (url || '').trim();
    if (isLocalMedia(href)) return `![${alt}](${figureUrl(href)})`;
    const guessed = inferLocalFigure(content, alt);
    return guessed ? `![${alt}](${guessed})` : `*${alt}*`;
  })
);

const ChatFigure = ({ src, alt }) => {
  const [failed, setFailed] = React.useState(false);
  if (!src || failed) {
    return alt ? <em className="chat-figure-fallback">{alt}</em> : null;
  }
  return (
    <figure className="chat-figure">
      <img src={src} alt={alt || ''} onError={() => setFailed(true)} />
      {alt ? <figcaption>{alt}</figcaption> : null}
    </figure>
  );
};

const ChatMessages = ({ messages }) => (
  <div className="chat-messages" aria-live="polite">
    {messages.map((message) => {
      const markdownComponents = {
        a: ({ href, children }) => (
          <a href={href} target={href?.startsWith('http') ? '_blank' : undefined} rel="noreferrer">
            {children}
          </a>
        ),
        img: ({ src, alt }) => (
          <ChatFigure
            src={isLocalMedia(src) ? figureUrl(src) : inferLocalFigure(message.content, alt)}
            alt={alt}
          />
        ),
      };

      return (
        <article key={message.id} className={`chat-message chat-message-${message.role}`}>
          <div className="chat-message-role">
            {message.role === 'user' ? 'You' : (
              <>
                <img src="/chat-logo.png" alt="" className="chat-avatar" />
                CoralFuture AI
              </>
            )}
          </div>
          <div className="chat-message-body">
            {Array.isArray(message.attachments) && message.attachments.length > 0 && (
              <div className="chat-message-attachments">
                {message.attachments.map((attachment) => (
                  attachment.isImage ? (
                    <a key={attachment.id || attachment.url} href={attachment.url} className="chat-attachment-image">
                      <img src={attachment.url} alt={attachment.originalName || 'attachment'} />
                    </a>
                  ) : (
                    <a key={attachment.id || attachment.url} href={attachment.url} className="chat-attachment-file">
                      {attachment.originalName || 'attachment'}
                    </a>
                  )
                ))}
              </div>
            )}
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {rewriteMarkdown(message.content || '')}
            </ReactMarkdown>
            {Array.isArray(message.links) && message.links.length > 0 && !message.streaming && (
              <div className="chat-links">
                {message.links.map((link) => (
                  <a key={link.href} href={link.href} className={`chat-link chat-link-${link.type || 'link'}`}>
                    {link.label || link.href}
                  </a>
                ))}
              </div>
            )}
          </div>
        </article>
      );
    })}
  </div>
);

export default ChatMessages;
